import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import {
  downloadZip,
  loadCommonData,
  loadFailureReasons, loadFailureReasonsSuccess,
  logout, requestBaseContracts, requestBaseContractsSuccess,
  requestCommonData, requestCrmSubAgents, requestCrmSubAgentsSuccess,
  reset, serverError,
  setCommonData,
  setPageTitle,
  unauthorizedError
} from './app-actions';
import {catchError, exhaustMap, filter, map, take, takeUntil, tap, withLatestFrom} from 'rxjs/operators';
import { CoreService } from '../services/core.service';
import { Store } from '@ngrx/store';
import { AppStoreState } from '../interfaces/app-store-state';
import {selectCommonData} from './app-selectors';
import { of} from 'rxjs';
import { Title } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { MessageService } from 'primeng/api';
import {DialogService} from "primeng/dynamicdialog";
import {LeadService} from "../services/lead.service";
import {EMPTY} from "rxjs/internal/observable/empty";
import {
  requestHotspotContractDictionary, requestHotspotContractDictionaryError,
  setHotspotContractDictionary
} from "../../dashboard/modules/hotspot/store/hotspot-contract-actions";
import {selectHotspotContractDictionary} from "../../dashboard/modules/hotspot/store/hotspot-selectors";
import {ToastService} from "../services/toast.service";
import {ErrorMessages} from "../../dashboard/utils/Errors";

@Injectable()
export class AppEffects {
	loadCommonData$ = createEffect(() =>
		this.actions$.pipe(
			ofType(loadCommonData),
			exhaustMap(() => this.coreService.getCommonData().pipe(
				takeUntil(this.actions$.pipe(ofType(logout))),
				map(response => setCommonData({commonData: response})),
				catchError(() => of(setCommonData({
					commonData: {
						is_authorized: false,
						user: null,
						user_info: null,
            captcha_error: null,
            captcha_required: null,
					}
				}))),
			)),
		)
	);

  requestCommonData$ = createEffect(() =>
		this.actions$.pipe(
			ofType(requestCommonData),
			withLatestFrom(this.store.select(selectCommonData)),
			filter(([action, commonData]) => commonData === undefined),
			map(() => loadCommonData()),
		)
	);

	setPageTitle$ = createEffect(() =>
		this.actions$.pipe(
			ofType(setPageTitle),
			map(action => {
				const newTitle = 'ЛК Агента' + (
					action.title !== undefined
						? ' | ' + action.title
						: ''
				);

				this.titleService.setTitle(newTitle);
			}),
		),
		{ dispatch: false }
	);

	logout$ = createEffect(() =>
		this.actions$.pipe(
			ofType(logout),
			map(() => {
				this.coreService.logout()
					.pipe(
            take(1),
            catchError(() => of(undefined))
          )
					.subscribe();

        this.dialogService.dialogComponentRefMap.forEach(dialog => {
          dialog.destroy();
        });

				this.store.dispatch(reset());

				this.router.navigate(['/login']);
			}),
		),
		{ dispatch: false }
	);

	unauthorizedError$ = createEffect(() =>
		this.actions$.pipe(
			ofType(unauthorizedError),
			map(() => logout()),
		)
	);

	serverError$ = createEffect(() =>
		this.actions$.pipe(
			ofType(serverError),
			tap(action => {
				this.messageService.add({
					severity: 'error',
					summary: 'Ошибка',
					detail: action.message ?? 'Серверная ошибка.',
          life: 10000,
				});
			}),
		),
		{ dispatch: false }
	);

  downloadZip$ = createEffect(() =>
      this.actions$.pipe(
        ofType(downloadZip),
        tap(action => {
          if (action.fileName.length < 1 || action.base64string.length < 1) return;

          const sliceSize = 512;
          const byteCharacters = atob(action.base64string);
          const byteArrays = [];

          for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
            const slice = byteCharacters.slice(offset, offset + sliceSize);
            const byteNumbers = new Array(slice.length);

            for (let i = 0; i < slice.length; i++) {
              byteNumbers[i] = slice.charCodeAt(i);
            }

            const byteArray = new Uint8Array(byteNumbers);
            byteArrays.push(byteArray);
          }

          const file = new Blob(byteArrays, {type: 'application/zip'});
          let link = document.createElement('a');
          link.href = URL.createObjectURL(file);
          link.download = action.fileName;
          link.click();
          link.remove();

        }),
      ),
    { dispatch: false }
  );

  requestSubAgents$ = createEffect(() =>
    this.actions$.pipe(
      ofType(requestCrmSubAgents),
      exhaustMap(() => this.leadService.requestSubAgents().pipe(
        takeUntil(this.actions$.pipe(ofType(logout))),
        map(response => requestCrmSubAgentsSuccess({subAgents: response})),
        catchError(() => {
          this.messageService.add({
            severity: 'error',
            summary: 'Ошибка',
            detail:  'Не удалось получить список агентов, повторите попытку позднее. В настоящий момент нет доступа до сервиса заявок.',
          });

          return of(requestCrmSubAgentsSuccess({subAgents: []}));
        })
      )),
    ),
  );

  loadFailureReasons$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadFailureReasons),
      exhaustMap(() => this.leadService.getFailureReasons().pipe(
        takeUntil(this.actions$.pipe(ofType(logout))),
        map(failureReasons => {
          return loadFailureReasonsSuccess({failureReasons: failureReasons})}),
        catchError((error) => {
          this.messageService.add({
            severity: 'error',
            summary: 'Ошибка',
            detail:  'Не удалось получить список причин отказа, повторите попытку позднее. В настоящий момент нет доступа до сервиса заявок.',
          });

          return EMPTY;
        }),
      )),
    ),
  );

  requestBaseContracts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(requestBaseContracts),
      exhaustMap((action) => this.leadService.requestBaseContracts(action.id).pipe(
        takeUntil(this.actions$.pipe(ofType(logout))),
        map(response => requestBaseContractsSuccess({ contracts: response ?? [] })),
        catchError(() => {
          this.toastService.errorToast(ErrorMessages.basedContractsLoadingError);

          return of(requestBaseContractsSuccess({ contracts: [] }))
        }),
      )),
    )
  );

	constructor(private actions$: Actions,
							private coreService: CoreService,
							private store: Store<{app: AppStoreState}>,
							private titleService: Title,
							private router: Router,
							private messageService: MessageService,
              private dialogService: DialogService,
              private leadService: LeadService,
              private toastService: ToastService
              ) { }

}
